/**
 * Rider library lineup + colors.
 * Combined stays uncolored (white/ink in the writer). Each billed act has its
 * own color, which the user can override.
 */

export const DEMO_TOUR_FOLDER_ID = 'tdemo_mtnwest26';

/** Demo Hawks tour: headliner + two openers (seed + live fallback). */
export const DEMO_TOUR_ACTS = [
  { id: 'headliner', name: 'Hawks', role: 'Headliner' },
  { id: 'opener-paper-cities', name: 'Paper Cities', role: 'Opener' },
  { id: 'opener-night-shift', name: 'Night Shift', role: 'Opener' },
];

/** Distinct band defaults. Combined is not in this list. */
export const RIDER_BAND_COLORS = [
  { light: '#c08133', dark: '#d9a05c' },
  { light: '#5a6fa8', dark: '#8296c4' },
  { light: '#c0483c', dark: '#d97066' },
  { light: '#4a6396', dark: '#8fa4d4' },
];

function cleanAct(raw, index) {
  const name = String(raw?.name || '').trim();
  if (!name) return null;
  const id = String(raw?.id || '').trim() || `act-${index}`;
  const role = String(raw?.role || '').trim() || (index === 0 ? 'Headliner' : 'Opener');
  return { id, name, role };
}

function normalizeHex(value, fallback) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

export function normalizeTourActs(tour, extras = {}) {
  const listed = Array.isArray(tour?.acts) ? tour.acts : [];
  const fromTour = listed.map(cleanAct).filter(Boolean);
  if (fromTour.length) return fromTour;
  if (extras.demoTour || extras.folderId === DEMO_TOUR_FOLDER_ID) {
    return DEMO_TOUR_ACTS.map((a) => ({ ...a }));
  }
  const name = String(extras.tourName || tour?.name || '').trim() || 'Headliner';
  return [{ id: 'headliner', name, role: 'Headliner' }];
}

/** Default color for the combined vehicle + parking rider. */
export const RIDER_VEHICLE_COLOR = { light: '#2a7d5c', dark: '#3aa87a' };

export const RIDER_VEHICLE_ID = 'vehicle-parking';

/** Always last in the rider library. One editable section, with its own color. */
export const RIDER_LIBRARY_FOOTER = [
  {
    id: RIDER_VEHICLE_ID,
    name: 'Vehicle Information & Parking',
    role: 'Bus, trailer, load-in, parking',
    pages: '',
    bandIndex: -1,
  },
];

export function buildRiderLibrary({
  tour,
  tourName,
  folderId,
  demoTour = false,
  riderFileCount = 0,
} = {}) {
  const acts = normalizeTourActs(tour, { tourName, folderId, demoTour });
  const combined = {
    id: 'combined',
    name: 'Combined package',
    role: 'All acts merged',
    pages: `${Math.max(1, Number(riderFileCount) || 0)} pg`,
    bandIndex: -1,
  };
  const bands = acts.map((act, i) => ({
    ...act,
    pages: '—',
    bandIndex: i,
  }));
  return [combined, ...bands, ...RIDER_LIBRARY_FOOTER.map((row) => ({ ...row }))];
}

/** Combined returns null (writer stays ink/white). Bands and vehicle/parking use override or default. */
export function riderLibraryColor(item, isDarkMode = false, overrides = {}) {
  if (!item || item.id === 'combined') return null;
  if (item.id === RIDER_VEHICLE_ID) {
    const def = isDarkMode ? RIDER_VEHICLE_COLOR.dark : RIDER_VEHICLE_COLOR.light;
    const custom = overrides && overrides[item.id];
    return custom ? normalizeHex(custom, def) : def;
  }
  if (item.pinned || item.bandIndex < 0) return null;
  const fallback = RIDER_BAND_COLORS[item.bandIndex % RIDER_BAND_COLORS.length];
  const def = isDarkMode ? fallback.dark : fallback.light;
  const custom = overrides && overrides[item.id];
  return custom ? normalizeHex(custom, def) : def;
}

export function applyRiderActColor(colorMap, actId, hex) {
  const id = String(actId || '').trim();
  if (!id || id === 'combined') return { ...(colorMap || {}) };
  const vehicle = id === RIDER_VEHICLE_ID;
  const next = { ...(colorMap || {}) };
  const fallback = vehicle ? RIDER_VEHICLE_COLOR.light : RIDER_BAND_COLORS[0].light;
  next[id] = normalizeHex(hex, fallback);
  return next;
}

export function nextRiderBandColor(currentHex, isDarkMode = false) {
  const palette = RIDER_BAND_COLORS.map((c) => (isDarkMode ? c.dark : c.light));
  const cur = String(currentHex || '').toLowerCase();
  const i = palette.findIndex((c) => c.toLowerCase() === cur);
  return palette[(i + 1) % palette.length];
}
